import { Box, Popover, SpaceBetween } from "@cloudscape-design/components";
import { mdiAirplaneTakeoff, mdiArrowRightThin, mdiClockStart } from "@mdi/js";
import Icon from "@mdi/react";
import { addDays, differenceInCalendarDays, differenceInMinutes, format, isAfter, isBefore, parse } from "date-fns";
import React from "react";

export interface RangeSliderProps {
    height?: number | string;
    style?: React.CSSProperties;
    className?: string;
    title?: string;
    evtStart?: string;
    evtEnd?: string;
    date?: string;
    start?: number,
    end?: number,
    key?: string,
    marginVertical?: number,
    showStart?: boolean,
    showEnd?: boolean,
    evtStartTH?: number,
    evtEndTH?: number,
    "aria-label"?: string;
}

/**
 * Spacer - a transparent div used to add vertical spacing.
 * If `height` is provided it will be strictly interpreted as pixels.
 */
export const RangeSlider: React.FC<RangeSliderProps> = ({
    height,
    style,
    start = 9,
    end = 19,
    date = format(new Date(), "dd-MM-yyyy"),
    evtStart = format(addDays(new Date(new Date().setHours(10, 0, 0, 0)), 1), "dd-MM-yyyy HH:mm"),
    evtEnd = format(addDays(new Date(new Date().setHours(14, 0, 0, 0)), 1), "dd-MM-yyyy HH:mm"),
    marginVertical = 20,
    key = "",
    title = "",
    showStart = true,
    showEnd = true,
    evtStartTH = 0,
    evtEndTH = 0,
    className,
    "aria-label": ariaLabel,
}) => {
    let heightStyle: React.CSSProperties = {};

    if (height !== undefined && height !== null) {
        // Accept number or numeric string. Force integer px.
        const numeric =
            typeof height === "number" ? height : parseFloat(String(height));
        if (Number.isFinite(numeric) && !Number.isNaN(numeric)) {
            // Use Math.max to avoid negative values, and Math.floor to enforce px integer
            const px = `${Math.max(0, Math.floor(numeric))}px`;
            heightStyle.height = px;
            heightStyle.minHeight = px; // ensure it doesn't collapse
            heightStyle.lineHeight = px; // keep layout predictable if needed
        } else {
            // Non-numeric input — warn and render flexible spacer (no explicit height)
            // eslint-disable-next-line no-console
            console.warn(
                "[Spacer] ignoring non-numeric height value:",
                height,
                " — expected number or numeric string (px)."
            );
        }
    }

    // Default to transparent block that can expand if no height provided.
    const combinedStyle: React.CSSProperties = {
        display: "block",
        width: "100%",
        background: "transparent",
        ...heightStyle,
        ...style, // user style merges last (but height already set above)
    };

    const fmt = "dd-MM-yyyy HH:mm";
    const dayFmt = "dd-MM-yyyy HH:mm";

    const eventStart = parse(evtStart, fmt, new Date());
    const eventEnd = parse(evtEnd, fmt, new Date());

    start = start - evtStartTH;
    end = end + evtEndTH;

    let multiDay = Math.abs(differenceInCalendarDays(eventEnd, eventStart));

    const dayStart = parse(`${date} ${start >= 1 ? start : start}:00`, dayFmt, new Date());
    const dayEnd = parse(`${date} ${end >= 23 ? end : end}:00`, dayFmt, new Date());

    const activeStart = isBefore(eventStart, dayStart) ? dayStart : eventStart;
    const activeEnd = isAfter(eventEnd, dayEnd) ? dayEnd : eventEnd;

    if (isAfter(activeStart, dayEnd) || isBefore(activeEnd, dayStart)) {
        //return { padding: 0, width: 0 }; // event not in range that day
    }

    const totalMinutes = (end - start) * 60;
    const paddingMinutes = differenceInMinutes(activeStart, dayStart);
    const activeMinutes = differenceInMinutes(activeEnd, activeStart);


    const widthPercentTH = (evtStartTH * 60 / totalMinutes) * 100;

    const paddingPercent = ((paddingMinutes / totalMinutes) * 100);
    const paddingPercentTH = paddingPercent - widthPercentTH;
    const widthPercent = (activeMinutes / totalMinutes) * 100;


    /*
        <div style={{ float: "left" }}><Icon path={mdiClockStart} size={0.75} /></div>
        */

    return (
        <>
            <div style={{ width: "99%", margin: marginVertical }} key={key}>
                <SpaceBetween direction="vertical" size="xs">
                    {/* Row with event times + optional takeoff icon + clock icon */}
                    <div style={{ width: "100%" }} key={"K1"}>
                        <div style={{ position: "relative", width: "100%" }}>
                            {/* Clock icon positioned relative to the green bar start */}
                            {widthPercentTH > 0 && (
                                <Popover
                                    triggerType="custom"
                                    content={`Umbral antes del vuelo: ${evtStartTH} hora${evtStartTH > 1 ? "s" : ""}`}
                                    dismissButton={false}
                                    position="top"
                                >
                                    <div
                                        style={{
                                            position: "absolute",
                                            left: `${paddingPercentTH}%`,
                                            top: "50%",
                                            transform: "translate(0%, -50%)",
                                            display: "inline-block",
                                            zIndex: 3,
                                        }}
                                    >
                                        <Icon path={mdiClockStart} size={1} color="#525252ff" />
                                    </div>
                                </Popover>
                            )}

                            {/* Texts and airplane icon */}
                            <div style={{ marginLeft: `${paddingPercent}%` }}>
                                <Box>
                                    {multiDay > 0 ? (
                                        <small>{format(eventStart, "dd MMM HH:mm")}</small>
                                    ) : evtStartTH > 0 ? (
                                        <>
                                            <Icon path={mdiAirplaneTakeoff} size={1} />{" "}
                                            <small>{format(eventStart, "HH:mm")}</small>
                                        </>
                                    ) : (
                                        <small>{format(eventStart, "HH:mm")}</small>
                                    )}
                                    <Icon path={mdiArrowRightThin} size={1} />
                                    <small>
                                        {multiDay > 0
                                            ? format(eventEnd, "dd MMM HH:mm")
                                            : format(eventEnd, "HH:mm")}
                                        {multiDay > 0
                                            ? ` (${multiDay} día${multiDay > 1 ? "s" : ""})`
                                            : null}
                                    </small>
                                </Box>
                            </div>
                        </div>
                    </div>

                    {/* Progress bars */}
                    <div
                        style={{
                            width: "100%",
                            height: 5,
                            backgroundColor: "light-dark(#ebebf0, #2f2f35)",
                            position: "relative",
                        }}
                        key={"K2"}
                    >
                        {/* Green bar */}
                        {widthPercentTH > 0 && (
                            <div
                                style={{
                                    position: "absolute",
                                    left: `${paddingPercentTH}%`,
                                    width: `${widthPercentTH}%`,
                                    height: 5,
                                    backgroundColor: "#1ac230ff",
                                    top: 0,
                                    zIndex: 1,
                                }}
                            />
                        )}

                        {/* Dark gray bar */}
                        <div
                            style={{
                                position: "absolute",
                                left: `${paddingPercent}%`,
                                width: `${widthPercent}%`,
                                height: 5,
                                backgroundColor: "light-dark(#2f2f35, #838383 )",
                                top: 0,
                                zIndex: 2,
                            }}
                        />
                    </div>

                    {/* Time markers below */}
                    {(showStart || showEnd) && (
                        <Box key={"K3"}>
                            {showStart && (
                                <div style={{ float: "left", width: "40%" }}>
                                    <Box variant="small" textAlign="left">
                                        {format(dayStart, "HH:mm")}
                                    </Box>
                                </div>
                            )}
                            {showEnd && (
                                <div style={{ float: "right", width: "40%", textAlign: "right" }}>
                                    <Box variant="small" textAlign="right">
                                        {format(dayEnd, "HH:mm")}
                                    </Box>
                                </div>
                            )}
                        </Box>
                    )}
                </SpaceBetween>
            </div>

        </>
    );
};

export default RangeSlider;
